import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES, CATEGORY_TYPES, EVENTS } from 'shared/constants';
import styles from './knowledge-category.module.scss';
import classnames from 'classnames';
import _ from 'lodash';
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import { Lang } from 'shared/constants/lang';
import { CategoryBoxComponent } from 'src/components/common/categories/category-box';
import { useViewSelector } from 'src/components/common/view-selector';
import { CategoryRowComponent } from 'src/components/common/categories/category-row';
import { useSelector } from 'react-redux';
import { getCategoryById, haveEditPermissions } from 'shared/store/selectors/knowledge.selector';
import { getParentIdByPath } from 'shared/utils/Utils';
import { isExternalSyncReadOnly } from 'shared/utils/knowledge.utils';
import { isCollaborator, isRoot } from 'shared/store/selectors/auth.selector';
import { isKnowledgeTreeCopyEnabled } from 'shared/store/selectors/features.selector';
import { ActionsDropdown } from 'src/components/common/actions-dropdown/actions-dropdown.component';
import { KnowledgeCategoryMovePopup } from './move-popup/knowledge-category-move';
import { KnowledgeCategoryMirrorPopup } from './mirror-popup/knowledge-category-mirror';
import { MetadataExtractionModal } from '../metadata-extraction';
import { ConfirmationPopupComponent } from 'src/components/common/confirmation-popup';
import { notification } from 'antd';
import Logger from 'shared/utils/Logger';
import { KnowledgeCategoryDeletePopup } from './delete-popup/knowledge-category-delete-popup.component';
import analyticsService from 'src/helpers/analytics.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { useMirrorActions } from '../mirror/use-mirror-actions';
import { CategoryMirrorBadge } from '../mirror/category-mirror-badge.component';
import { useMirroredInTargets } from '../mirror/use-mirrored-in-targets';
import { IconAnchor } from 'src/components/design-system/buttons/icon-anchor';

const KnowledgeCategory = React.memo(function KnowledgeCategory({
  id,
  title = '',
  subtitle,
  knowledgeItems = [],
  itemsLastUpdatedAt = null,
  knowledgeSelectItem = () => {},
  activeId = null,
  adminMode = false,
  deleteCategory = () => {},
  detachMirroredCategory = () => {},
  updateCategory = () => {},
  editCategory = () => {},
  dragItem = () => {},
  findItem = () => {},
  finishDrag = () => {},
  dndGroup,
  type,
  ownerIds = [],
  path,
  tags,
  metadata = {},
  options,
  lang = Lang.ACTION_BUTTON_TOOLTIPS,
  labels,
  shared,
  mirroredFrom,
  mirroredFromInfo,
  mirroredFromSourceLink,
  mirroredFromWorkspaceId,
}) {
  const navigate = useNavigate();
  const { goToSourceAction, sourceHref, mirrorLang, canAccessSourceWorkspace } = useMirrorActions({
    mirroredFromSourceLink,
    mirroredFromWorkspaceId,
  });
  const mirroredInTargets = useMirroredInTargets(id);
  const [isMoveOpen, setMoveOpen] = useState(false);
  const [isMirrorOpen, setMirrorOpen] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDetachConfirmOpen, setDetachConfirmOpen] = useState(false);
  const [deleteEntity, setDeleteEntity] = useState(null);
  const [metadataExtractionOpen, setMetadataExtractionOpen] = useState(false);
  const isCategory = knowledgeItems.length > 0 || type === CATEGORY_TYPES.KNOWLEDGE;
  const isReadOnly = isExternalSyncReadOnly(options);
  let focusStyle = !_.isNull(activeId) ? (activeId === id ? styles.active : styles.inActive) : null;

  const originalIndex = findItem(id)?.index;

  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));
  const isCollaboratorUser = useSelector(isCollaborator);
  const isRootUser = useSelector(isRoot);
  const isCopyEnabled = useSelector(isKnowledgeTreeCopyEnabled);
  const parentId = getParentIdByPath(path);
  const isNestedMirror = useSelector(
    state => !!mirroredFrom && !!getCategoryById(state.knowledge, { id: parentId })?.mirroredFrom,
  );

  const [{ isDragging }, drag] = useDrag({
    item: { type: dndGroup, id, originalIndex },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => adminMode && !isNestedMirror && !isReadOnly,
    end: (dropResult, monitor) => {
      const { id: droppedId, originalIndex } = monitor.getItem();
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        dragItem(droppedId, originalIndex);
      }

      finishDrag(droppedId);
    },
  });
  // Hovering over a source-derived sibling snaps the dragged item back to its
  // original index so it never appears reordered while the cursor is over the
  // non-droppable area.
  const [, drop] = useDrop({
    accept: dndGroup,
    canDrop: () => false,
    hover({ id: draggedId, originalIndex: draggedOriginalIndex }) {
      if (isNestedMirror) {
        dragItem(draggedId, draggedOriginalIndex);
        return;
      }
      if (draggedId !== id) {
        const { index: overIndex } = findItem(id);
        dragItem(draggedId, overIndex);
      }
    },
  });

  // While a sibling is being dragged in this dnd group, source-derived categories
  // dim to signal they aren't a valid drop target.
  const isDragInGroupActive = useDragLayer(
    monitor => monitor.isDragging() && monitor.getItem()?.type === dndGroup,
  );
  const dimmedAsSourceDerived = isNestedMirror && isDragInGroupActive;

  const onClick = React.useCallback(() => {
    if (isCategory) {
      knowledgeSelectItem(id);
      return;
    }

    navigate(ROUTES.CONTENT_ITEM.replace(':id', id));
  }, [navigate, id, isCategory, knowledgeSelectItem]);

  const onBlur = React.useCallback(
    e => {
      const { textContent: value } = e.target;

      if (value === title) return false;

      updateCategory({ id, title: value, ownerIds });
    },
    [id, title, updateCategory, ownerIds],
  );

  const deleteCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();
      if (!hasEditPermission) return;
      if (mirroredFrom) {
        setDeleteConfirmOpen(true);
        return;
      }
      setDeleteEntity({ id, title, type, path, ownerIds });
    },
    [hasEditPermission, mirroredFrom, id, title, type, path, ownerIds],
  );

  const confirmDeleteMirror = React.useCallback(() => {
    deleteCategory({ id });
    setDeleteConfirmOpen(false);
  }, [deleteCategory, id]);

  const detachMirrorAction = React.useCallback(() => {
    if (!hasEditPermission) return;
    setDetachConfirmOpen(true);
  }, [hasEditPermission]);

  const confirmDetachMirror = React.useCallback(async () => {
    setDetachConfirmOpen(false);
    try {
      await detachMirroredCategory({ id });
      notification.success({
        message: mirrorLang.DETACH_MIRROR_SUCCESS,
        placement: 'bottomRight',
      });
    } catch (error) {
      Logger.error('Detach mirror failed:', { error });
      notification.error({
        message: mirrorLang.DETACH_MIRROR_ERROR,
        placement: 'bottomRight',
      });
    }
  }, [detachMirroredCategory, id, mirrorLang]);

  const moveCategoryAction = React.useCallback(
    e => {
      if (!hasEditPermission) {
        return;
      }

      setMoveOpen(true);
    },
    [hasEditPermission],
  );

  const mirrorCategoryAction = React.useCallback(
    e => {
      if (!hasEditPermission) {
        return;
      }

      setMirrorOpen(true);
    },
    [hasEditPermission],
  );

  const editCategoryAction = React.useCallback(
    e => {
      if (!hasEditPermission) {
        return;
      }
      editCategory({
        id,
        title,
        subtitle,
        ownerIds,
        path,
        type,
        tags,
        options,
        labels,
        shared,
      });
      analyticsService.track(EVENTS.CATEGORIES.EDIT, {
        categoryId: id,
      });
    },
    [
      hasEditPermission,
      editCategory,
      id,
      title,
      subtitle,
      ownerIds,
      path,
      type,
      tags,
      options,
      labels,
      shared,
    ],
  );

  const extractMetadataAction = React.useCallback(() => {
    setMetadataExtractionOpen(true);
  }, []);

  const actionDisabledOnMirrorTooltip =
    mirrorLang[
      `ACTION_DISABLED_FOR_MIRRORED_${mirroredFromInfo?.sourceCategoryType?.toUpperCase()}`
    ];
  const cannotMirrorAMirrorTooltip =
    mirrorLang[
      `MIRROR_ACTION_DISABLED_FOR_MIRRORED_${mirroredFromInfo?.sourceCategoryType?.toUpperCase()}`
    ];

  const adminActions = [
    {
      label: lang.EDIT,
      onClick: editCategoryAction,
      tooltip: mirroredFrom
        ? actionDisabledOnMirrorTooltip
        : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission || !!mirroredFrom,
    },
    {
      label: lang.MOVE,
      onClick: moveCategoryAction,
      tooltip: isReadOnly
        ? lang.READ_ONLY_SYNCED
        : mirroredFrom
          ? actionDisabledOnMirrorTooltip
          : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission || !!mirroredFrom || isReadOnly,
    },
    {
      label: lang.MIRROR,
      onClick: mirrorCategoryAction,
      tooltip: mirroredFrom
        ? cannotMirrorAMirrorTooltip
        : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission || !!mirroredFrom,
      props: { hidden: !isCollaboratorUser || !hasEditPermission || !isCopyEnabled },
    },
    {
      label: lang.DELETE,
      onClick: deleteCategoryAction,
      tooltip:
        isReadOnly && !mirroredFrom
          ? lang.READ_ONLY_SYNCED
          : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      // Deleting a mirror removes only the mirror doc, never the synced source.
      disabled: !hasEditPermission || (isReadOnly && !mirroredFrom),
    },
    {
      label: lang.EXTRACT_METADATA,
      onClick: extractMetadataAction,
      tooltip: mirroredFrom && actionDisabledOnMirrorTooltip,
      disabled: !!mirroredFrom,
      props: { hidden: !isRootUser },
    },
    {
      label: lang.DETACH_MIRROR,
      onClick: detachMirrorAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_DETACH,
      disabled: !hasEditPermission,
      props: { hidden: !mirroredFrom || isNestedMirror },
    },
  ];

  let opacity = 1;
  if (isDragging) {
    opacity = 0;
  } else if (dimmedAsSourceDerived) {
    opacity = 0.5;
  }

  const { isGridView } = useViewSelector();
  const CategoryComponent = isGridView ? CategoryBoxComponent : CategoryRowComponent;

  return (
    <div
      ref={node => drag(drop(node))}
      style={{ opacity }}
      className={classnames(
        isGridView ? styles.box : {},
        focusStyle,
        adminMode ? styles.admin : {},
      )}>
      <CategoryComponent
        title={title}
        tags={tags}
        labels={labels}
        adminMode={adminMode}
        hasEditPermission={hasEditPermission && !isReadOnly}
        type={type}
        onClick={onClick}
        onBlur={onBlur}
        badge={
          <CategoryMirrorBadge
            adminMode={adminMode}
            isCollaboratorUser={isCollaboratorUser}
            mirroredFrom={mirroredFrom}
            mirroredFromInfo={mirroredFromInfo}
            mirroredInTargets={mirroredInTargets}
            mirrorLang={mirrorLang}
            className={styles.mirrorBadge}
            iconClassName={styles.mirrorBadgeIcon}
          />
        }
        actions={
          adminMode && (
            <>
              <ActionsDropdown actions={adminActions} />
              {isCollaboratorUser && mirroredFrom && canAccessSourceWorkspace && (
                <IconAnchor
                  label={
                    mirrorLang[
                      `GO_TO_SOURCE_${mirroredFromInfo?.sourceCategoryType?.toUpperCase()}`
                    ]
                  }
                  href={sourceHref}
                  onClick={e => {
                    e.stopPropagation();
                    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                    e.preventDefault();
                    goToSourceAction();
                  }}
                  className={styles.goToSourceButton}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </IconAnchor>
              )}
            </>
          )
        }
        lastUpdatedAt={itemsLastUpdatedAt}
        categoryType={CATEGORY_TYPES.KNOWLEDGE}
        metadata={metadata}
        options={{ ...options, knowledgeItems }}
        renderItemOptions={{
          showLabels: true,
          showTags: true,
          showMetadata: true,
        }}
      />
      <KnowledgeCategoryMovePopup
        isOpen={isMoveOpen}
        categoryId={id}
        id={id}
        onFinish={() => setMoveOpen(false)}
      />
      <KnowledgeCategoryMirrorPopup
        isOpen={isMirrorOpen}
        id={id}
        onFinish={() => setMirrorOpen(false)}
      />
      <MetadataExtractionModal
        open={metadataExtractionOpen}
        onClose={() => setMetadataExtractionOpen(false)}
        categoryId={id}
        categoryTitle={title}
      />
      <ConfirmationPopupComponent
        open={isDeleteConfirmOpen}
        title={mirrorLang.DELETE_MIRROR_TITLE}
        message={mirrorLang.DELETE_MIRROR_MESSAGE}
        onConfirm={confirmDeleteMirror}
        onDismiss={() => setDeleteConfirmOpen(false)}
      />
      <ConfirmationPopupComponent
        open={isDetachConfirmOpen}
        title={mirrorLang.DETACH_MIRROR_TITLE}
        message={mirrorLang.DETACH_MIRROR_MESSAGE}
        onConfirm={confirmDetachMirror}
        onDismiss={() => setDetachConfirmOpen(false)}
      />
      <KnowledgeCategoryDeletePopup
        isOpen={!!deleteEntity}
        entity={deleteEntity}
        deleteCategory={deleteCategory}
        onFinish={() => setDeleteEntity(null)}
      />
    </div>
  );
});

export { KnowledgeCategory };
